/**
 * Worker ⇄ UI message types and validators. See docs/event-model.md §9.
 *
 * Both sides validate what they receive. Every message except `ready` (and an
 * `error` raised while booting) carries the `runId` it belongs to, so the UI
 * can ignore stragglers from a cancelled run.
 *
 * `layers` carries newly revealed dynamic-code layers (Phase 3); the UI
 * accepts them in any order relative to the events that refer to them.
 * Not yet covered: `approval` /
 * `approval-request` (Phase 5).
 */
import { type CanaryRegistry, isCanaryRegistry } from "../canary";
import { parseSimContext, type SimContext } from "../context";
import {
  type AuditEvent,
  type ConsoleEntry,
  type GlobalChange,
  isAuditEvent,
  isConsoleEntry,
  isGlobalChange,
  isLayer,
  isObject,
  isStringArray,
  type Layer,
} from "../events";

export interface EngineInfo {
  name: string;
  version: string;
}

// ── UI → worker ─────────────────────────────────────────────────────────────

/** How to evaluate the code (ADR-0015). Phase 1 only runs classic scripts. */
export type RunMode = "auto" | "script" | "module";

export interface RunMessage {
  type: "run";
  runId: string;
  code: string;
  mode: RunMode;
  context: SimContext;
  /** Seeds the guest PRNG and canaries; with the code and context it fixes the run. */
  seed: number;
  environment: string;
}

/** Asks the worker to stop cooperatively. The UI still terminates it as a fallback. */
export interface CancelMessage {
  type: "cancel";
  runId: string;
}

export type UiMessage = RunMessage | CancelMessage;

// ── Worker → UI ─────────────────────────────────────────────────────────────

/** Sent once the worker has loaded the WASM and booted the prelude, before any run. */
export interface ReadyMessage {
  type: "ready";
  engine: EngineInfo;
  environments: string[];
}

/** An engine-internal failure, distinct from exceptions thrown by guest code. */
export interface ErrorMessage {
  type: "error";
  /** Absent if the worker failed before any run started. */
  runId?: string;
  message: string;
}

export interface EventsMessage {
  type: "events";
  runId: string;
  events: AuditEvent[];
}

/** Newly revealed code layers, in creation order (docs/sandbox.md §3.6). */
export interface LayersMessage {
  type: "layers";
  runId: string;
  layers: Layer[];
}

export interface ConsoleMessage {
  type: "console";
  runId: string;
  entries: ConsoleEntry[];
}

export interface RunStats {
  events: number;
  /** Virtual time the run covered. */
  virtualMs: number;
  /** Wall-clock time spent executing guest code. */
  cpuMs: number;
}

export interface DoneMessage {
  type: "done";
  runId: string;
  stats: RunStats;
  /** Ids of the limits that stopped the run early, e.g. `cpu` or `memory`. */
  limitsHit: string[];
  /** The final document's HTML, for the Page preview. */
  html?: string;
  /**
   * The canaries the engine generated and planted for this run
   * (core/canary), which analysis needs to turn `AuditEvent.canaries` hits
   * into data flows.
   */
  canaries?: CanaryRegistry;
  /** The end-of-run global/intrinsic diff against the boot-time snapshot (ADR-0012). */
  globalDiff?: GlobalChange[];
}

export type WorkerMessage =
  | ReadyMessage
  | ErrorMessage
  | EventsMessage
  | LayersMessage
  | ConsoleMessage
  | DoneMessage;

// ── Validators ──────────────────────────────────────────────────────────────

const isString = (v: unknown): v is string => typeof v === "string";
const isNonNegative = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= 0;

const isEngineInfo = (v: unknown): v is EngineInfo =>
  isObject(v) && isString(v.name) && isString(v.version);

const isRunStats = (v: unknown): v is RunStats =>
  isObject(v) &&
  Number.isInteger(v.events) &&
  isNonNegative(v.events) &&
  isNonNegative(v.virtualMs) &&
  isNonNegative(v.cpuMs);

const isRunMode = (v: unknown): v is RunMode => v === "auto" || v === "script" || v === "module";

/** Validates data received from the worker; returns null if it isn't a known message. */
export function parseWorkerMessage(data: unknown): WorkerMessage | null {
  if (!isObject(data)) return null;
  switch (data.type) {
    case "ready":
      return isEngineInfo(data.engine) && isStringArray(data.environments)
        ? {
            type: "ready",
            engine: { name: data.engine.name, version: data.engine.version },
            environments: data.environments,
          }
        : null;
    case "error":
      if (!isString(data.message)) return null;
      if (data.runId === undefined) return { type: "error", message: data.message };
      return isString(data.runId)
        ? { type: "error", runId: data.runId, message: data.message }
        : null;
    case "events":
      return isString(data.runId) && Array.isArray(data.events) && data.events.every(isAuditEvent)
        ? { type: "events", runId: data.runId, events: data.events }
        : null;
    case "layers":
      return isString(data.runId) && Array.isArray(data.layers) && data.layers.every(isLayer)
        ? { type: "layers", runId: data.runId, layers: data.layers }
        : null;
    case "console":
      return isString(data.runId) &&
        Array.isArray(data.entries) &&
        data.entries.every(isConsoleEntry)
        ? { type: "console", runId: data.runId, entries: data.entries }
        : null;
    case "done":
      return isString(data.runId) &&
        isRunStats(data.stats) &&
        isStringArray(data.limitsHit) &&
        (data.html === undefined || isString(data.html)) &&
        (data.canaries === undefined || isCanaryRegistry(data.canaries)) &&
        (data.globalDiff === undefined ||
          (Array.isArray(data.globalDiff) && data.globalDiff.every(isGlobalChange)))
        ? {
            type: "done",
            runId: data.runId,
            stats: {
              events: data.stats.events,
              virtualMs: data.stats.virtualMs,
              cpuMs: data.stats.cpuMs,
            },
            limitsHit: data.limitsHit,
            ...(isString(data.html) && { html: data.html }),
            ...(data.canaries !== undefined && { canaries: data.canaries as CanaryRegistry }),
            ...(data.globalDiff !== undefined && {
              globalDiff: data.globalDiff as GlobalChange[],
            }),
          }
        : null;
    default:
      return null;
  }
}

/** Validates data received from the UI; returns null if it isn't a known message. */
export function parseUiMessage(data: unknown): UiMessage | null {
  if (!isObject(data) || !isString(data.runId)) return null;
  switch (data.type) {
    case "run": {
      const context = parseSimContext(data.context);
      return isString(data.code) &&
        isRunMode(data.mode) &&
        context &&
        Number.isSafeInteger(data.seed) &&
        isString(data.environment)
        ? {
            type: "run",
            runId: data.runId,
            code: data.code,
            mode: data.mode,
            context,
            seed: data.seed as number,
            environment: data.environment,
          }
        : null;
    }
    case "cancel":
      return { type: "cancel", runId: data.runId };
    default:
      return null;
  }
}
